/**
 * Three generic handler factories that cover a large fraction of the
 * legacy tool catalog. Each is a small function that produces a
 * ChiroToolHandler for a specific toolId + label combo.
 *
 * Tool-shape → factory mapping (from analysis of uChiroToolsWizard1.cpp):
 *   distanceHandler    — 2 clicks, line drawn, distance shown in mm
 *   angle3PointHandler — 3 clicks, vertex angle at the middle click
 *   angle4PointHandler — 4 clicks, angle between two constructed lines
 *
 * Tools whose intent DOES NOT map cleanly to these shapes (Meyerding's
 * grading, Iliac Index ratios, Gonstead's 16-step protocol, and other
 * multi-purpose ones) are deliberately NOT wired to these — they get
 * bespoke handlers when their time comes.
 */
import type { ChiroToolHandler, ChiroDrawable } from '../types';
import {
  distance, angle3Point, angle4Point, extendLine, midpoint,
} from '../math';

const LINE_COLOR = '#ffd400';
const LABEL_STYLE = { color: LINE_COLOR, fontSize: 16 };

export function distanceHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 2,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        out.push({ kind: 'line', a: points[0], b: points[1], style: { color: LINE_COLOR } });
        const d = distance(points[0], points[1]);
        out.push({
          kind: 'label',
          at: midpoint(points[0], points[1]),
          text: `${d.toFixed(1)} mm`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const d = distance(points[0], points[1]);
      return {
        summary: `${label}: ${d.toFixed(1)} mm`,
        rows: [{ label, value: `${d.toFixed(1)} mm` }],
      };
    },
  };
}

export function angle3PointHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 3,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        out.push({ kind: 'line', a: points[0], b: points[1], style: { color: LINE_COLOR } });
      }
      if (points.length >= 3) {
        out.push({ kind: 'line', a: points[1], b: points[2], style: { color: LINE_COLOR } });
        const a = angle3Point(points[0], points[1], points[2]);
        out.push({ kind: 'label', at: points[1], text: `${a.toFixed(1)}°`, style: LABEL_STYLE });
      }
      return out;
    },
    compute(points) {
      const a = angle3Point(points[0], points[1], points[2]);
      return {
        summary: `${label}: ${a.toFixed(1)}°`,
        rows: [{ label, value: `${a.toFixed(1)}°` }],
      };
    },
  };
}

export function angle4PointHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 4,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        const l1 = extendLine(points[0], points[1], 0.5);
        out.push({ kind: 'line', a: l1.a, b: l1.b, style: { color: LINE_COLOR } });
      }
      if (points.length >= 4) {
        const l2 = extendLine(points[2], points[3], 0.5);
        out.push({ kind: 'line', a: l2.a, b: l2.b, style: { color: LINE_COLOR } });
        const angle = angle4Point(points[0], points[1], points[2], points[3]);
        const at = midpoint(midpoint(points[0], points[1]), midpoint(points[2], points[3]));
        out.push({ kind: 'label', at, text: `${angle.toFixed(1)}°`, style: LABEL_STYLE });
      }
      return out;
    },
    compute(points) {
      const angle = angle4Point(points[0], points[1], points[2], points[3]);
      return {
        summary: `${label}: ${angle.toFixed(1)}°`,
        rows: [{ label, value: `${angle.toFixed(1)}°` }],
      };
    },
  };
}
